/**
 * thoraxCore.js — лёгкие, тело и область сердца на КТ груди. [12.0.52]
 *
 * Чистая математика: без DOM, без Cornerstone. Зовут её ui/clinicalTools.js
 * (эмфизема, КТИ, Агатстон) и ai/aiModule.js (кальциевый счёт для всех кнопок).
 * Проверка — tests/thorax_test.mjs на синтетической грудной клетке.
 *
 * ПОВОД. До 12.0.52 «Эмфизема» считала ВСЕ воксели −1000…−200 HU во всём
 * томе: воздух вокруг пациента внутри поля, газ в кишке и желудке, трахею.
 * КТИ называл «сердцем» всё 0…500 HU в полосе кадра — мышцы груди, печень,
 * позвоночник. Агатстон брал «кальций» в прямоугольнике посреди кадра —
 * туда попадали позвонки, грудина и рёбра. Все три ответа не зависели от
 * того, есть ли на снимке лёгкие и сердце вообще. Здесь — общая основа:
 * сначала найти тело и лёгкие, и только потом считать.
 *
 * КАК (каждый шаг — один проход по тому, память — 1 байт на воксель):
 *  1. Тело на каждом срезе: воздух, связанный с краем кадра, — снаружи;
 *     из остального берётся крупнейшая связная часть (стол и руки — прочь).
 *  2. Воздух внутри тела (< −400 HU) — кандидаты; связные куски в 3D.
 *     Лёгкие — крупнейший кусок и второй, если он не меньше трети первого
 *     (два лёгких поровну; газ желудка/кишки — много меньше). Кусок
 *     со средней плотностью ниже −960 HU — чистый газ, а не лёгкое.
 *  3. Трахея и главные бронхи вычитаются: рост от трахеи по < −950 HU
 *     с потолком объёма (не удалось — сказано словами, а не скрыто).
 *  4. Мелкие «дыры» в маске на срезе (сосуды, очаги до 4 см²) заливаются:
 *     они — часть лёгкого, так считают все программы денситометрии.
 *
 * Пороги −400 / −950 / −960 HU, «треть», 4 см², 300 мл — инженерные, ПО
 * ПАМЯТИ (типичные значения в литературе по сегментации лёгких), не
 * выверены на наборе снимков. Источники клинических порогов — у функций.
 *
 * Коды в массиве `код` (Uint8Array по вокселям):
 *   0 — вне тела (воздух вокруг, стол);  1 — ткань тела не-лёгкое;
 *   2 — лёгкое;  3 — дыхательные пути (вычтены);  4 — залитая дыра лёгкого;
 *   5 — газ внутри тела, не лёгкое (желудок, кишка).
 *
 * EXPORTS: КОД, сегментироватьЛёгкие, эмфизема, кти, областьСердца, сторонаПациента
 */

export const КОД = Object.freeze({ ВНЕ: 0, ТЕЛО: 1, ЛЁГКОЕ: 2, ПУТИ: 3, ДЫРА: 4, ГАЗ: 5 });

const ПОРОГ_ВОЗДУХА = -400;      // по памяти: −400…−500 HU — обычная граница «воздух/ткань» для масок лёгких
const ПОРОГ_ПУТЕЙ = -950;        // просвет трахеи и бронхов
const ГАЗ_НЕ_ЛЁГКОЕ = -960;      // средняя плотность чистого газа; лёгкое плотнее
const ДЫРА_ММ2 = 400;            // 4 см²
const ПУТИ_МАКС_МЛ = 300;
const ЛЁГКИЕ_МИН_МЛ = 100;
const ВНЕ_ПОЛЯ = -1500;          // пиксели за кругом реконструкции (−2000, −3024 и т.п.)

/** Растущий стек индексов (Int32Array, удвоение по нужде). */
function стек(нач = 1 << 16) {
  let м = new Int32Array(нач), n = 0;
  return {
    push(v) { if (n === м.length) { const н = new Int32Array(м.length * 2); н.set(м); м = н; } м[n++] = v; },
    pop() { return м[--n]; },
    get size() { return n; },
    clear() { n = 0; },
  };
}

/**
 * Сторона пациента для смещения по оси x индекса.
 * В DICOM (LPS) +x мира — к ЛЕВОМУ боку пациента. Направление оси индекса i
 * в мире — direction[0..2] (Cornerstone: строка, столбец, нормаль подряд).
 * @returns {1|-1} +1 — рост индекса x ведёт к левому боку пациента, −1 — к правому
 */
export function сторонаПациента(direction) {
  const dx = Array.isArray(direction) || ArrayBuffer.isView(direction) ? Number(direction[0]) : 1;
  return dx < 0 ? -1 : 1;
}

/** Вверх по индексу z — это к голове? (нормаль среза по z мира) */
function zКГолове(direction) {
  const d = direction && direction.length >= 9 ? Number(direction[8]) : 1;
  return !(d < 0);
}

/**
 * Найти тело и лёгкие.
 * @param {ArrayLike<number>} hu — значения HU, порядок x быстрее y быстрее z
 * @param {number[]} dims — [nx, ny, nz]
 * @param {number[]} spacing — мм
 * @param {{direction?: number[]}} [opts]
 * @returns {{ok:true, код:Uint8Array, лёгкиеМл:number, кусков:number, пути:{удалены:boolean, мл:number, причина?:string},
 *            zОт:number, zДо:number, предупреждения:string[]}
 *          |{ok:false, причина:string}}
 */
export function сегментироватьЛёгкие(hu, dims, spacing, opts = {}) {
  const [nx, ny, nz] = dims || [];
  if (!(nx > 2 && ny > 2 && nz >= 1) || !hu || hu.length < nx * ny * nz) {
    return { ok: false, причина: 'нет данных объёма нужного размера' };
  }
  const [sx, sy, sz] = (spacing || [1, 1, 1]).map((v) => (v > 0 ? v : 1));
  const срез = nx * ny;
  const N = срез * nz;
  const vox = sx * sy * sz;
  const код = new Uint8Array(N);
  const предупреждения = [];
  // Пиксели края поля (внутри тела). Лёгкое на краю — поле режет лёгкое.
  const уКрая = new Uint8Array(N);

  // ── 1. Тело по срезам.
  //
  // «Край» среза — пиксели у границы кадра и у области вне круга
  // реконструкции (значение ≤ −1500 HU: так сканеры метят пиксели за полем).
  // Обычная КТ груди: на краю — воздух вокруг пациента, заливка от края по
  // воздуху даёт «снаружи». КТ сердца с малым полем (200–250 мм): поле режет
  // грудь, на краю — мягкие ткани и лёгкие; заливать от края по воздуху
  // нельзя — уйдут лёгкие. Признак такого среза: на краю ≥ 25% тканей.
  // Тогда «снаружи» — только пиксели за полем.
  // Циклы по срезу написаны «плоско» — без замыканий, на общем стеке
  // Int32Array: на томе 512×512×300 это втрое быстрее (замер 12.0.52).
  const снаружи = new Uint8Array(срез);
  const метка = new Int32Array(срез);
  const край = new Uint8Array(срез);
  const ст = new Int32Array(срез * 2 + 4);
  const s = стек();
  let обрезанныхСрезов = 0;
  for (let z = 0; z < nz; z++) {
    const o = z * срез;
    снаружи.fill(0);
    край.fill(0);
    // За полем — сразу «снаружи»; есть ли такие пиксели вообще.
    let заПолемЕсть = false;
    for (let k = 0; k < срез; k++) if (hu[o + k] <= ВНЕ_ПОЛЯ) { снаружи[k] = 1; заПолемЕсть = true; }
    let наКраю = 0, тканьНаКраю = 0;
    const отметить = (k) => { if (!снаружи[k] && !край[k]) { край[k] = 1; наКраю++; if (hu[o + k] >= ПОРОГ_ВОЗДУХА) тканьНаКраю++; } };
    for (let x = 0; x < nx; x++) { отметить(x); отметить((ny - 1) * nx + x); }
    for (let y = 1; y < ny - 1; y++) { отметить(y * nx); отметить(y * nx + nx - 1); }
    if (заПолемЕсть) {
      for (let k = 0; k < срез; k++) {
        if (снаружи[k]) continue;
        const x = k % nx;
        if ((x > 0 && снаружи[k - 1]) || (x < nx - 1 && снаружи[k + 1])
          || (k >= nx && снаружи[k - nx]) || (k + nx < срез && снаружи[k + nx])) отметить(k);
      }
    }
    const обрезан = наКраю > 0 && тканьНаКраю >= 0.25 * наКраю;
    if (обрезан) обрезанныхСрезов++;
    else {
      let n = 0;
      for (let k = 0; k < срез; k++) if (край[k] && hu[o + k] < ПОРОГ_ВОЗДУХА) { снаружи[k] = 1; ст[n++] = k; }
      while (n) {
        const k = ст[--n], x = k % nx;
        let j;
        if (x > 0) { j = k - 1; if (!снаружи[j] && hu[o + j] < ПОРОГ_ВОЗДУХА) { снаружи[j] = 1; ст[n++] = j; } }
        if (x < nx - 1) { j = k + 1; if (!снаружи[j] && hu[o + j] < ПОРОГ_ВОЗДУХА) { снаружи[j] = 1; ст[n++] = j; } }
        if (k >= nx) { j = k - nx; if (!снаружи[j] && hu[o + j] < ПОРОГ_ВОЗДУХА) { снаружи[j] = 1; ст[n++] = j; } }
        if (k + nx < срез) { j = k + nx; if (!снаружи[j] && hu[o + j] < ПОРОГ_ВОЗДУХА) { снаружи[j] = 1; ст[n++] = j; } }
      }
    }
    for (let k = 0; k < срез; k++) if (край[k] && !снаружи[k]) уКрая[o + k] = 1;
    // Крупнейшая связная часть «не снаружи».
    метка.fill(0);
    let лучшая = 0, лучшийРазмер = 0, номер = 0;
    for (let k0 = 0; k0 < срез; k0++) {
      if (снаружи[k0] || метка[k0]) continue;
      номер++;
      let размер = 0, n = 0;
      метка[k0] = номер; ст[n++] = k0;
      while (n) {
        const k = ст[--n]; размер++;
        const x = k % nx;
        let j;
        if (x > 0) { j = k - 1; if (!снаружи[j] && !метка[j]) { метка[j] = номер; ст[n++] = j; } }
        if (x < nx - 1) { j = k + 1; if (!снаружи[j] && !метка[j]) { метка[j] = номер; ст[n++] = j; } }
        if (k >= nx) { j = k - nx; if (!снаружи[j] && !метка[j]) { метка[j] = номер; ст[n++] = j; } }
        if (k + nx < срез) { j = k + nx; if (!снаружи[j] && !метка[j]) { метка[j] = номер; ст[n++] = j; } }
      }
      if (размер > лучшийРазмер) { лучшийРазмер = размер; лучшая = номер; }
    }
    if (лучшая) {
      for (let k = 0; k < срез; k++) if (метка[k] === лучшая) код[o + k] = hu[o + k] < ПОРОГ_ВОЗДУХА ? КОД.ГАЗ : КОД.ТЕЛО;
    }
  }

  // ── 2. Связные куски газа внутри тела (6-связность). Временно метим 6.
  const куски = [];   // {семя, вокселей, сумма}
  const ВРЕМ = 6;
  for (let i0 = 0; i0 < N; i0++) {
    if (код[i0] !== КОД.ГАЗ) continue;
    let вокс = 0, сумма = 0;
    s.clear(); код[i0] = ВРЕМ; s.push(i0);
    while (s.size) {
      const i = s.pop(); вокс++; сумма += hu[i];
      const x = i % nx, t = (i - x) / nx, y = t % ny, z = (t - y) / ny;
      if (x > 0 && код[i - 1] === КОД.ГАЗ) { код[i - 1] = ВРЕМ; s.push(i - 1); }
      if (x < nx - 1 && код[i + 1] === КОД.ГАЗ) { код[i + 1] = ВРЕМ; s.push(i + 1); }
      if (y > 0 && код[i - nx] === КОД.ГАЗ) { код[i - nx] = ВРЕМ; s.push(i - nx); }
      if (y < ny - 1 && код[i + nx] === КОД.ГАЗ) { код[i + nx] = ВРЕМ; s.push(i + nx); }
      if (z > 0 && код[i - срез] === КОД.ГАЗ) { код[i - срез] = ВРЕМ; s.push(i - срез); }
      if (z < nz - 1 && код[i + срез] === КОД.ГАЗ) { код[i + срез] = ВРЕМ; s.push(i + срез); }
    }
    куски.push({ семя: i0, вокселей: вокс, средняя: сумма / вокс });
  }
  const похожиНаЛёгкое = куски.filter((к) => к.средняя > ГАЗ_НЕ_ЛЁГКОЕ && к.вокселей * vox / 1000 >= ЛЁГКИЕ_МИН_МЛ / 2)
    .sort((a, b) => b.вокселей - a.вокселей);
  const взять = [];
  if (похожиНаЛёгкое[0]) взять.push(похожиНаЛёгкое[0]);
  if (похожиНаЛёгкое[1] && похожиНаЛёгкое[1].вокселей * 3 >= похожиНаЛёгкое[0].вокселей) взять.push(похожиНаЛёгкое[1]);
  const мл = взять.reduce((a, к) => a + к.вокселей, 0) * vox / 1000;
  if (!взять.length || мл < ЛЁГКИЕ_МИН_МЛ) {
    return { ok: false, причина: 'лёгкие на снимке не найдены (нет крупного воздушного пространства внутри тела с плотностью лёгочной ткани)' };
  }
  // Выбранные куски → ЛЁГКОЕ, прочее временное → ГАЗ.
  for (const к of взять) {
    s.clear(); код[к.семя] = КОД.ЛЁГКОЕ; s.push(к.семя);
    while (s.size) {
      const i = s.pop();
      const x = i % nx, t = (i - x) / nx, y = t % ny, z = (t - y) / ny;
      if (x > 0 && код[i - 1] === ВРЕМ) { код[i - 1] = КОД.ЛЁГКОЕ; s.push(i - 1); }
      if (x < nx - 1 && код[i + 1] === ВРЕМ) { код[i + 1] = КОД.ЛЁГКОЕ; s.push(i + 1); }
      if (y > 0 && код[i - nx] === ВРЕМ) { код[i - nx] = КОД.ЛЁГКОЕ; s.push(i - nx); }
      if (y < ny - 1 && код[i + nx] === ВРЕМ) { код[i + nx] = КОД.ЛЁГКОЕ; s.push(i + nx); }
      if (z > 0 && код[i - срез] === ВРЕМ) { код[i - срез] = КОД.ЛЁГКОЕ; s.push(i - срез); }
      if (z < nz - 1 && код[i + срез] === ВРЕМ) { код[i + срез] = КОД.ЛЁГКОЕ; s.push(i + срез); }
    }
  }
  for (let i = 0; i < N; i++) if (код[i] === ВРЕМ) код[i] = КОД.ГАЗ;

  // Границы лёгких по z.
  let zОт = nz, zДо = -1;
  for (let z = 0; z < nz; z++) {
    const o = z * срез;
    for (let k = 0; k < срез; k++) if (код[o + k] === КОД.ЛЁГКОЕ) { if (z < zОт) zОт = z; zДо = z; break; }
  }

  // ── 3. Трахея: ищем у головного конца лёгких, растим по < −950 HU.
  const пути = вычестьПути(hu, код, dims, [sx, sy, sz], zОт, zДо, zКГолове(opts.direction), s);
  if (!пути.удалены) предупреждения.push(`дыхательные пути не вычтены: ${пути.причина}`);
  // Трахея над верхушками лёгких вычтена — границы по z считаем заново.
  zОт = nz; zДо = -1;
  for (let z = 0; z < nz; z++) {
    const o = z * срез;
    for (let k = 0; k < срез; k++) if (код[o + k] === КОД.ЛЁГКОЕ) { if (z < zОт) zОт = z; zДо = z; break; }
  }

  // ── 4. Мелкие дыры в маске лёгкого на срезе.
  const предел = Math.max(1, Math.round(ДЫРА_ММ2 / (sx * sy)));
  const видно = new Uint8Array(срез);
  const дыра = new Int32Array(срез);
  for (let z = zОт; z <= zДо; z++) {
    const o = z * срез;
    видно.fill(0);
    let n = 0;
    const лёгкое = (k) => код[o + k] === КОД.ЛЁГКОЕ;
    for (let x = 0; x < nx; x++) for (const k of [x, (ny - 1) * nx + x]) if (!видно[k] && !лёгкое(k)) { видно[k] = 1; ст[n++] = k; }
    for (let y = 1; y < ny - 1; y++) for (const k of [y * nx, y * nx + nx - 1]) if (!видно[k] && !лёгкое(k)) { видно[k] = 1; ст[n++] = k; }
    while (n) {
      const k = ст[--n], x = k % nx;
      let j;
      if (x > 0) { j = k - 1; if (!видно[j] && код[o + j] !== КОД.ЛЁГКОЕ) { видно[j] = 1; ст[n++] = j; } }
      if (x < nx - 1) { j = k + 1; if (!видно[j] && код[o + j] !== КОД.ЛЁГКОЕ) { видно[j] = 1; ст[n++] = j; } }
      if (k >= nx) { j = k - nx; if (!видно[j] && код[o + j] !== КОД.ЛЁГКОЕ) { видно[j] = 1; ст[n++] = j; } }
      if (k + nx < срез) { j = k + nx; if (!видно[j] && код[o + j] !== КОД.ЛЁГКОЕ) { видно[j] = 1; ст[n++] = j; } }
    }
    // Недостижимое с края и не лёгкое — дыра. Заливаем, если мала.
    for (let k0 = 0; k0 < срез; k0++) {
      if (видно[k0] || код[o + k0] === КОД.ЛЁГКОЕ) continue;
      let m = 0;
      видно[k0] = 1; ст[n++] = k0;
      while (n) {
        const k = ст[--n]; дыра[m++] = k;
        const x = k % nx;
        let j;
        if (x > 0) { j = k - 1; if (!видно[j] && код[o + j] !== КОД.ЛЁГКОЕ) { видно[j] = 1; ст[n++] = j; } }
        if (x < nx - 1) { j = k + 1; if (!видно[j] && код[o + j] !== КОД.ЛЁГКОЕ) { видно[j] = 1; ст[n++] = j; } }
        if (k >= nx) { j = k - nx; if (!видно[j] && код[o + j] !== КОД.ЛЁГКОЕ) { видно[j] = 1; ст[n++] = j; } }
        if (k + nx < срез) { j = k + nx; if (!видно[j] && код[o + j] !== КОД.ЛЁГКОЕ) { видно[j] = 1; ст[n++] = j; } }
      }
      if (m <= предел) {
        for (let q = 0; q < m; q++) { const i = o + дыра[q]; if (код[i] !== КОД.ПУТИ) код[i] = КОД.ДЫРА; }
      }
    }
  }

  let вЛёгких = 0, лёгкоеУКрая = 0;
  for (let i = 0; i < N; i++) {
    if (код[i] === КОД.ЛЁГКОЕ || код[i] === КОД.ДЫРА) { вЛёгких++; if (уКрая[i]) лёгкоеУКрая++; }
  }
  const обрезано = лёгкоеУКрая > 0;
  if (обрезано) предупреждения.push('поле обзора режет лёгкие — в счёт вошла только попавшая в кадр часть');
  return {
    ok: true, код, лёгкиеМл: вЛёгких * vox / 1000, кусков: взять.length, пути,
    zОт, zДо, предупреждения, обрезано, обрезанныхСрезов,
  };
}

function вычестьПути(hu, код, dims, spacing, zОт, zДо, кГолове, s) {
  const [nx, ny, nz] = dims;
  const [sx, sy, sz] = spacing;
  const срез = nx * ny;
  const vox = sx * sy * sz;
  if (zДо < zОт) return { удалены: false, мл: 0, причина: 'лёгких нет' };
  // Тело по x на срезе — для «трахея посередине».
  const глубина = Math.max(1, Math.round(40 / sz));      // ищем в 40 мм от головного конца
  const площадьОт = 50 / (sx * sy), площадьДо = 700 / (sx * sy);
  const порядок = [];
  for (let n = 0; n < глубина; n++) порядок.push(кГолове ? zДо - n : zОт + n);
  const видно = new Uint8Array(срез);
  for (const z of порядок) {
    if (z < 0 || z >= nz) continue;
    const o = z * срез;
    let xMin = nx, xMax = -1;
    for (let k = 0; k < срез; k++) { const c = код[o + k]; if (c !== КОД.ВНЕ) { const x = k % nx; if (x < xMin) xMin = x; if (x > xMax) xMax = x; } }
    if (xMax < 0) continue;
    const третьОт = xMin + (xMax - xMin) / 3, третьДо = xMin + 2 * (xMax - xMin) / 3;
    видно.fill(0);
    for (let k0 = 0; k0 < срез; k0++) {
      if (видно[k0] || код[o + k0] !== КОД.ЛЁГКОЕ || !(hu[o + k0] < ПОРОГ_ПУТЕЙ)) continue;
      const список = [];
      s.clear(); видно[k0] = 1; s.push(k0);
      while (s.size) {
        const k = s.pop(); список.push(k);
        const x = k % nx, y = (k - x) / nx;
        const ш = (kk) => { if (!видно[kk] && код[o + kk] === КОД.ЛЁГКОЕ && hu[o + kk] < ПОРОГ_ПУТЕЙ) { видно[kk] = 1; s.push(kk); } };
        if (x > 0) ш(k - 1); if (x < nx - 1) ш(k + 1);
        if (y > 0) ш(k - nx); if (y < ny - 1) ш(k + nx);
      }
      if (список.length < площадьОт || список.length > площадьДо) continue;
      let cx = 0; for (const k of список) cx += k % nx; cx /= список.length;
      if (cx < третьОт || cx > третьДо) continue;
      // Нашли сечение трахеи — растим в 3D с потолком.
      const предел = Math.round(ПУТИ_МАКС_МЛ * 1000 / vox);
      const путь = [];
      s.clear();
      for (const k of список) { const i = o + k; код[i] = КОД.ПУТИ; путь.push(i); s.push(i); }
      let утекло = false;
      while (s.size) {
        const i = s.pop();
        const x = i % nx, t = (i - x) / nx, y = t % ny, zz = (t - y) / ny;
        const ш = (j) => { if (код[j] === КОД.ЛЁГКОЕ && hu[j] < ПОРОГ_ПУТЕЙ) { код[j] = КОД.ПУТИ; путь.push(j); s.push(j); } };
        if (x > 0) ш(i - 1); if (x < nx - 1) ш(i + 1);
        if (y > 0) ш(i - nx); if (y < ny - 1) ш(i + nx);
        if (zz > 0) ш(i - срез); if (zz < nz - 1) ш(i + срез);
        if (путь.length > предел) { утекло = true; break; }
      }
      if (утекло) {
        for (const i of путь) код[i] = КОД.ЛЁГКОЕ;
        return { удалены: false, мл: 0, причина: `рост от трахеи вышел за ${ПУТИ_МАКС_МЛ} мл — просвет бронхов сливается с лёгочной тканью ниже −950 HU (бывает при выраженной эмфиземе); трахея и бронхи остались в счёте` };
      }
      return { удалены: true, мл: путь.length * vox / 1000 };
    }
  }
  return { удалены: false, мл: 0, причина: 'трахея у верхушек лёгких не найдена (серия может начинаться ниже бифуркации)' };
}

/** Перцентиль по гистограмме целых HU (−1200…+600). */
function перцентиль(гист, всего, доля, низ) {
  const цель = всего * доля;
  let нак = 0;
  for (let b = 0; b < гист.length; b++) { нак += гист[b]; if (нак >= цель) return b + низ; }
  return гист.length - 1 + низ;
}

/**
 * Денситометрия эмфиземы по маске лёгких.
 *
 * Показатели — ПО ПАМЯТИ, источники: LAA−950 (доля вокселей лёгкого ниже
 * −950 HU) — Gevenois PA et al., AJRCCM 1996 (сопоставление с гистологией на
 * тонких срезах); P15 (15-й перцентиль плотности) — Dirksen A et al., 1999.
 * Порог «эмфизема есть» LAA−950 ≥ 5% — так в исследовании COPDGene. Пишем
 * это как ориентир, а не как диагноз: значения зависят от вдоха, толщины
 * среза, ядра реконструкции и контраста.
 *
 * @param {ArrayLike<number>} hu
 * @param {number[]} dims
 * @param {number[]} spacing
 * @param {{код: Uint8Array}} лёгкие — результат сегментироватьЛёгкие
 * @param {{direction?: number[]}} [opts]
 */
export function эмфизема(hu, dims, spacing, лёгкие, opts = {}) {
  if (!лёгкие?.ok) return { ok: false, причина: лёгкие?.причина || 'нет маски лёгких' };
  const [nx, ny, nz] = dims;
  const vox = spacing[0] * spacing[1] * spacing[2];
  const код = лёгкие.код;
  const НИЗ = -1200, гист = new Float64Array(1801);
  const стороны = { правое: { в: 0, н: 0 }, левое: { в: 0, н: 0 } };
  const знак = сторонаПациента(opts.direction);
  const срез = nx * ny;
  let всего = 0, ниже950 = 0, сумма = 0;
  for (let z = 0; z < nz; z++) {
    const o = z * срез;
    // Середина тела на срезе по x — граница правого и левого лёгкого.
    let xMin = nx, xMax = -1;
    for (let k = 0; k < срез; k++) if (код[o + k] !== КОД.ВНЕ) { const x = k % nx; if (x < xMin) xMin = x; if (x > xMax) xMax = x; }
    const середина = (xMin + xMax) / 2;
    for (let k = 0; k < срез; k++) {
      const c = код[o + k];
      if (c !== КОД.ЛЁГКОЕ && c !== КОД.ДЫРА) continue;
      const v = hu[o + k];
      всего++; сумма += v;
      const b = Math.max(0, Math.min(1800, Math.round(v) - НИЗ));
      гист[b]++;
      const низко = v < -950;
      if (низко) ниже950++;
      const x = k % nx;
      const сторона = (x - середина) * знак < 0 ? стороны.правое : стороны.левое;
      сторона.в++; if (низко) сторона.н++;
    }
  }
  if (!всего) return { ok: false, причина: 'маска лёгких пуста' };
  const предупреждения = [...(лёгкие.предупреждения || [])];
  if (spacing[2] > 2) предупреждения.push(`срезы через ${spacing[2].toFixed(1)} мм: показатели денситометрии выверены на тонких (≤ 1,5 мм) срезах, на толстых доля эмфиземы занижается`);
  const пр = (с) => (с.в ? +(100 * с.н / с.в).toFixed(1) : null);
  return {
    ok: true,
    laa950: +(100 * ниже950 / всего).toFixed(1),
    p15: перцентиль(гист, всего, 0.15, НИЗ),
    объёмМл: Math.round(всего * vox / 1000),
    средняяHU: Math.round(сумма / всего),
    вокселей: всего,
    правое: { laa950: пр(стороны.правое), мл: Math.round(стороны.правое.в * vox / 1000) },
    левое: { laa950: пр(стороны.левое), мл: Math.round(стороны.левое.в * vox / 1000) },
    путиВычтены: !!лёгкие.пути?.удалены,
    предупреждения,
  };
}

/**
 * По срезам: на каждой строке — медиальный край правого лёгкого и медиальный
 * край левого, и наружные края. Координата u растёт к левому боку пациента.
 */
function строкиЛёгких(код, dims, direction) {
  const [nx, ny, nz] = dims;
  const срез = nx * ny;
  const знак = сторонаПациента(direction);
  const u = (x) => (знак > 0 ? x : nx - 1 - x);
  const площадь = { правое: new Int32Array(nz), левое: new Int32Array(nz) };
  const середина = new Float64Array(nz);
  for (let z = 0; z < nz; z++) {
    const o = z * срез;
    let uMin = nx, uMax = -1;
    for (let k = 0; k < срез; k++) if (код[o + k] !== КОД.ВНЕ) { const uu = u(k % nx); if (uu < uMin) uMin = uu; if (uu > uMax) uMax = uu; }
    середина[z] = (uMin + uMax) / 2;
    for (let k = 0; k < срез; k++) {
      const c = код[o + k];
      if (c !== КОД.ЛЁГКОЕ && c !== КОД.ДЫРА) continue;
      if (u(k % nx) < середина[z]) площадь.правое[z]++; else площадь.левое[z]++;
    }
  }
  return { u, середина, площадь };
}

/**
 * КТИ на осевых срезах — аналог рентгеновского кардиоторакального индекса.
 *
 * Поперечник сердца — наибольшее расстояние по горизонтали между медиальными
 * краями правого и левого лёгкого (в него входит перикард и жир около
 * сердца) — на строках в средней полосе (20…70% переднезаднего размаха
 * лёгких, считая спереди). Поперечник груди — наибольшее расстояние между
 * наружными краями лёгких (внутренний поперечник грудной клетки). Полоса и
 * 30% — эвристика, проверенная на синтетике, а не на наборе снимков.
 * Ищем на «средних» срезах,
 * где оба лёгких занимают не меньше 30% своей наибольшей площади: у купола
 * диафрагмы между лёгкими лежат печень и желудок, а не сердце.
 *
 * Порог 0,50 — рентгенографический (Danzer, 1919; ПО ПАМЯТИ). Для осевой КТ
 * он не выверен: КТ снимается лёжа и на вдохе, поперечник сердца меряется
 * не в той проекции. Поэтому вывод — число и ориентир, а не «кардиомегалия».
 */
export function кти(hu, dims, spacing, лёгкие, opts = {}) {
  if (!лёгкие?.ok) return { ok: false, причина: лёгкие?.причина || 'нет маски лёгких' };
  if (лёгкие.обрезано && !opts.дажеОбрезанное) {
    return { ok: false, причина: 'поле обзора режет грудную клетку (похоже на КТ сердца с малым полем) — поперечник груди по такому снимку не измерить' };
  }
  const [nx, ny, nz] = dims;
  const код = лёгкие.код;
  const срез = nx * ny;
  const { u, середина, площадь } = строкиЛёгких(код, dims, opts.direction);
  let максП = 0, максЛ = 0;
  for (let z = 0; z < nz; z++) { if (площадь.правое[z] > максП) максП = площадь.правое[z]; if (площадь.левое[z] > максЛ) максЛ = площадь.левое[z]; }
  if (!максП || !максЛ) return { ok: false, причина: 'нашлось только одно лёгкое — поперечник сердца между лёгкими не измерить' };
  let сердце = 0, zСердца = -1, грудь = 0, zГруди = -1, срезов = 0;
  const щельСреза = new Float64Array(nz);
  const минСтрока = Math.max(1, Math.round(20 / spacing[0]));     // 20 мм лёгкого на строке
  // В LPS рост y — к спине; direction[4] — проекция оси y индекса на y мира.
  const спередиМалыйY = !(opts.direction && opts.direction.length >= 9 && Number(opts.direction[4]) < 0);
  for (let z = 0; z < nz; z++) {
    if (площадь.правое[z] < 0.3 * максП || площадь.левое[z] < 0.3 * максЛ) continue;
    срезов++;
    const o = z * срез;
    let наружПр = Infinity, наружЛв = -Infinity;
    // Полоса строк: 20…70% переднезаднего размаха лёгких, считая спереди.
    // Спереди лёгкие сходятся над сердцем, сзади обнимают позвоночник; у
    // самых кончиков медиальный край уходит вбок, и щель там шире сердца.
    let yMin = ny, yMax = -1;
    for (let k = 0; k < срез; k++) { const c = код[o + k]; if (c === КОД.ЛЁГКОЕ || c === КОД.ДЫРА) { const y = (k - k % nx) / nx; if (y < yMin) yMin = y; if (y > yMax) yMax = y; } }
    const размах = yMax - yMin;
    const yA = спередиМалыйY ? yMin + 0.2 * размах : yMax - 0.7 * размах;
    const yB = спередиМалыйY ? yMin + 0.7 * размах : yMax - 0.2 * размах;
    for (let y = 0; y < ny; y++) {
      let медПр = -Infinity, медЛв = Infinity, чПр = 0, чЛв = 0;
      for (let x = 0; x < nx; x++) {
        const c = код[o + y * nx + x];
        if (c !== КОД.ЛЁГКОЕ && c !== КОД.ДЫРА) continue;
        const uu = u(x);
        if (uu < середина[z]) { чПр++; if (uu > медПр) медПр = uu; if (uu < наружПр) наружПр = uu; }
        else { чЛв++; if (uu < медЛв) медЛв = uu; if (uu > наружЛв) наружЛв = uu; }
      }
      // Строка годится, только если оба лёгких на ней не «кончик»: у
      // передних и задних кончиков медиальный край уходит вбок, и щель
      // между лёгкими там шире сердца.
      if (y >= yA && y <= yB && медПр > -Infinity && медЛв < Infinity && чПр >= минСтрока && чЛв >= минСтрока) {
        const щель = (медЛв - медПр - 1) * spacing[0];
        if (щель > сердце) { сердце = щель; zСердца = z; }
        if (щель > щельСреза[z]) щельСреза[z] = щель;
      }
    }
    if (наружПр < Infinity && наружЛв > -Infinity) {
      const ш = (наружЛв - наружПр + 1) * spacing[0];
      if (ш > грудь) { грудь = ш; zГруди = z; }
    }
  }
  if (!срезов || !(грудь > 0) || !(сердце > 0)) return { ok: false, причина: 'нет срезов, где оба лёгкие достаточно велики' };
  // Срез «середины сердца»: середина ряда срезов, где щель не меньше 95%
  // наибольшей (первый же такой срез — край сердца, а не середина).
  const близкие = [];
  for (let z = 0; z < nz; z++) if (щельСреза[z] >= 0.95 * сердце) близкие.push(z);
  if (близкие.length) zСердца = близкие[Math.floor(близкие.length / 2)];
  return { ok: true, кти: +(сердце / грудь).toFixed(2), сердцеМм: +сердце.toFixed(1), грудьМм: +грудь.toFixed(1), zСердца, zГруди, срезов };
}

/** Выпуклая оболочка точек (монотонная цепь). Точки [x, y]. */
function оболочка(точки) {
  if (точки.length < 3) return точки.slice();
  const p = точки.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const крест = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const низ = [], верх = [];
  for (const т of p) { while (низ.length >= 2 && крест(низ[низ.length - 2], низ[низ.length - 1], т) <= 0) низ.pop(); низ.push(т); }
  for (let i = p.length - 1; i >= 0; i--) { const т = p[i]; while (верх.length >= 2 && крест(верх[верх.length - 2], верх[верх.length - 1], т) <= 0) верх.pop(); верх.push(т); }
  низ.pop(); верх.pop();
  return низ.concat(верх);
}

/** Пересечение строки y с выпуклым многоугольником → [xОт, xДо] или null. */
function строкаМногоугольника(мн, y) {
  if (!мн.length) return null;
  let от = Infinity, до = -Infinity;
  for (let i = 0; i < мн.length; i++) {
    const a = мн[i], b = мн[(i + 1) % мн.length];
    if ((a[1] - y) * (b[1] - y) > 0) continue;
    if (a[1] === b[1]) { от = Math.min(от, a[0], b[0]); до = Math.max(до, a[0], b[0]); continue; }
    const x = a[0] + (y - a[1]) * (b[0] - a[0]) / (b[1] - a[1]);
    от = Math.min(от, x); до = Math.max(до, x);
  }
  return от <= до ? [от, до] : null;
}

/**
 * Область сердца и средостения для поиска коронарного кальция и проверка
 * контраста.
 *
 * Область — выпуклая оболочка обоих лёгких на срезе, объединённая с
 * оболочками соседних срезов в пределах ±20 мм (низ сердца лежит на
 * диафрагме, где лёгкие уже узкие), внутри тела, без самих лёгких. В неё
 * попадают и некоронарные кальцинаты: клапан, кольцо митрального клапана,
 * стенка аорты, хрящи у грудины. Кости (позвонки, грудина, рёбра)
 * отсекаются уже при счёте — по размеру связного куска (calciumScoreCore,
 * крупнееМм3).
 *
 * Контраст: медиана плотности мягких тканей области (−50…1000 HU). Без
 * контраста кровь и миокард ~40 HU; с контрастом полости сердца и аорта
 * 250…500 HU. Порог 120 HU — ПО ПАМЯТИ, с большим запасом в обе стороны.
 * Индекс Агатстона определён только для бесконтрастной КТ (Agatston AS et
 * al., JACC 1990): контраст в просвете сам выше 130 HU.
 *
 * @returns {{ok:true, от:Int16Array, до:Int16Array, разрешено:Uint8Array, разрешёнКод:number, медианаHU:number, контраст:boolean}
 *          |{ok:false, причина:string}}
 *   от/до — границы области по x на каждой строке (индекс z*ny+y), от > до — пусто;
 *   разрешено/разрешёнКод — воксель годится, только если разрешено[i] === разрешёнКод
 *   (ткань тела, не лёгкое и не снаружи).
 */
export function областьСердца(hu, dims, spacing, лёгкие) {
  if (!лёгкие?.ok) return { ok: false, причина: лёгкие?.причина || 'нет маски лёгких' };
  const [nx, ny, nz] = dims;
  const код = лёгкие.код;
  const срез = nx * ny;
  const оболочки = new Array(nz).fill(null);
  for (let z = лёгкие.zОт; z <= лёгкие.zДо; z++) {
    const o = z * срез;
    const точки = [];
    for (let y = 0; y < ny; y++) {
      let a = -1, b = -1;
      for (let x = 0; x < nx; x++) {
        const c = код[o + y * nx + x];
        if (c === КОД.ЛЁГКОЕ || c === КОД.ДЫРА) { if (a < 0) a = x; b = x; }
      }
      if (a >= 0) { точки.push([a, y]); if (b !== a) точки.push([b, y]); }
    }
    if (точки.length >= 3) оболочки[z] = оболочка(точки);
  }
  const окно = Math.max(0, Math.round(20 / (spacing[2] || 1)));
  // Контраст меряем на уровне сердца — там, где щель между лёгкими шире
  // всего (тот же поиск, что у КТИ), ±15 мм. Выше и ниже сердца между
  // лёгкими лежат трахея, жир, печень: их плотность о контрасте не скажет.
  const к = кти(hu, dims, spacing, лёгкие, { дажеОбрезанное: true });
  const zК = к.ok ? к.zСердца : -1;
  const полуокноК = Math.max(0, Math.round(15 / (spacing[2] || 1)));
  const от = new Int16Array(nz * ny).fill(1), до = new Int16Array(nz * ny).fill(0);
  const гист = new Float64Array(1051);    // −50…1000
  let всего = 0;
  for (let z = 0; z < nz; z++) {
    for (let y = 0; y < ny; y++) {
      let a = Infinity, b = -Infinity;
      for (let zz = Math.max(0, z - окно); zz <= Math.min(nz - 1, z + окно); zz++) {
        const мн = оболочки[zz];
        if (!мн) continue;
        const р = строкаМногоугольника(мн, y);
        if (р) { if (р[0] < a) a = р[0]; if (р[1] > b) b = р[1]; }
      }
      if (a > b) continue;
      const xa = Math.max(0, Math.ceil(a)), xb = Math.min(nx - 1, Math.floor(b));
      if (xa > xb) continue;
      от[z * ny + y] = xa; до[z * ny + y] = xb;
      if (zК >= 0 && Math.abs(z - zК) > полуокноК) continue;
      const o = z * срез + y * nx;
      for (let x = xa; x <= xb; x++) {
        if (код[o + x] !== КОД.ТЕЛО) continue;
        const v = hu[o + x];
        if (v < -50 || v > 1000) continue;
        гист[Math.round(v) + 50]++; всего++;
      }
    }
  }
  if (!всего) return { ok: false, причина: 'между лёгкими не нашлось мягких тканей' };
  const медианаHU = перцентиль(гист, всего, 0.5, -50);
  // Отдельный массив «разрешено» не заводим: это тот же код, значение ТЕЛО.
  return { ok: true, от, до, разрешено: код, разрешёнКод: КОД.ТЕЛО, медианаHU, контраст: медианаHU > 120 };
}

export default { КОД, сегментироватьЛёгкие, эмфизема, кти, областьСердца, сторонаПациента };
